/** @jsx jsx */
import { jsx, Flex, Box } from 'theme-ui';
import { Themed } from '@theme-ui/mdx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '../components/Button';
import FullHeightColumn from '../components/FullHeightColumn';
import { goBack, goNext, selectDeliveryMethod } from '../state/parcelFlow';
import ServiceCard from './ServiceCard';
import { ariaLabelForService } from '../utils/shipment';

const ServiceList = ({ services, selectedOption }) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const container = useRef();
  const [focusIndex, setFocusIndex] = useState(0);
  const onSelect = useCallback(
    (serviceCode) => {
      dispatch(selectDeliveryMethod(serviceCode));
    },
    [dispatch]
  );

  return (
    <Box ref={container} sx={{ maxWidth: 640 }} role="group" aria-label={translate('buyParcel.services.title')}>
      {services.map((s, index) => {
        const isSelected = s.serviceCode === selectedOption;
        return (
          <Box
            sx={{ display: 'relative', width: '100%', my: 2 }}
            key={s.serviceCode}
            onClick={() => onSelect(s.serviceCode)}
            className="service"
            role="checkbox"
            aria-label={ariaLabelForService(s, translate)}
            aria-checked={isSelected}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onSelect(s.serviceCode);
              }
            }}
            tabIndex={0}
          >
            <ServiceCard service={s} isSelected={isSelected} onClick={() => onSelect(s.serviceCode)} />
          </Box>
        );
      })}
    </Box>
  );
};

export const deliveryAgreedTimeOption = {
  serviceCode: 'KJ',
  displayName: 'Kotijakelu',
  price: 0,
  priceVat0: 0,
};

const deliveryOptions = [
  {
    serviceCode: 'JA',
    displayName: 'Jakelu vastaanottajalle',
    price: 0,
    priceVat0: 0,
  },
  deliveryAgreedTimeOption,
  {
    serviceCode: 'NMH',
    displayName: 'Nouto Matkahuollosta',
    price: 0,
    priceVat0: 0,
  },
];

const enabledOptions = ['JA', 'KJ'];

export default () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const onBackClick = useCallback(() => dispatch(goBack()), [dispatch]);
  const onNextClick = useCallback(() => dispatch(goNext()), [dispatch]);
  const selectedOption = useSelector((state) => state.parcelFlow.delivery);
  const options = useMemo(() => deliveryOptions.filter((o) => enabledOptions.includes(o.serviceCode)), []);

  return (
    <FullHeightColumn>
      <Box>
        <Button onClick={onBackClick} variant="plain" sx={{ color: 'primary' }}>
          {translate('buyParcel.backButton')}
        </Button>
      </Box>

      <Themed.h1 sx={{ color: 'secondary', mt: 0 }}>{translate('buyParcel.delivery.title')}</Themed.h1>

      <Flex
        sx={{
          flex: ['auto', null, 'none'],
          flexDirection: 'column',
          justifyContent: ['space-between'],
        }}
      >
        <ServiceList services={options} selectedOption={selectedOption} />
        <Button
          sx={{
            alignSelf: ['flex-end', null, 'flex-start'],
            mt: 3,
          }}
          onClick={onNextClick}
          disabled={!selectedOption}
        >
          {translate('buyParcel.continue')}
        </Button>
      </Flex>
    </FullHeightColumn>
  );
};
